import React, { useEffect, lazy, memo, createElement } from 'react';
import { Star, Heart, Check, ShieldCheck, Sparkles, MapPin, Instagram, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Section } from '../components/ui/Section';
import { Accordion } from '../components/ui/Accordion';
import { PriceCard } from '../components/ui/PriceCard';
import { AnimatedFlagBackground } from '../components/AnimatedFlagBackground';
import { StickySpotCounter } from '../components/StickySpotCounter';
import { SEO } from '../components/SEO';
import { BackgroundMusicToggle } from '../components/BackgroundMusicToggle';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';
export function SalesPage() {
  const {
    isPlaying,
    toggle,
    duck,
    unduck
  } = useBackgroundMusic();
  useEffect(() => {
    // Update page title
    document.title = 'Aquí entre Mujeres by Kate Castrillon';
    // Update favicon dynamically
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = 'https://cdn.magicpatterns.com/uploads/jomajo3SSHHmkh5xF5a7j2/KATE_4Landingpage.png';
  }, []);
  const scrollToOffer = () => {
    const element = document.getElementById('price-card');
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  return <div className="min-h-screen w-full bg-brand-ivory selection:bg-brand-clay/30 selection:text-brand-clay relative font-sans text-brand-text" data-id="element-76">
      <SEO title="Aquí entre Mujeres by Kate Castrillon" description="Un encuentro íntimo y profundamente femenino para la mujer que ya no quiere seguir igual. Evento presencial en Costa Rica." image="https://cdn.magicpatterns.com/uploads/jomajo3SSHHmkh5xF5a7j2/KATE_4Landingpage.png" data-id="element-77" />
      <BackgroundMusicToggle isPlaying={isPlaying} onToggle={toggle} data-id="element-78" />
      <AnimatedFlagBackground data-id="element-79" />

      {/* HERO SECTION */}
      <header className="relative w-full overflow-hidden bg-brand-dark/85 backdrop-blur-[2px] text-white z-10 shadow-xl" data-id="element-80">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/60 via-transparent to-brand-dark/80 mix-blend-multiply" data-id="element-81"></div>

        <div className="relative max-w-6xl mx-auto px-4 pt-20 pb-16 md:pt-28 md:pb-24 flex flex-col md:flex-row items-center gap-12" data-id="element-82">
          <div className="flex-1 text-center md:text-left z-10" data-id="element-83">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/20 text-white text-xs font-medium tracking-widest uppercase mb-6 backdrop-blur-sm" data-id="element-84">
              <MapPin className="h-3 w-3" data-id="element-85" /> Evento Presencial — Costa Rica
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6 text-brand-ivory" data-id="element-86">
              Este espacio no es para todas... <br data-id="element-87" />
              <span className="text-brand-clay italic" data-id="element-88">¿pero es para ti?</span>
            </h1>
            <p className="text-lg md:text-xl text-brand-pistachio mb-8 max-w-xl leading-relaxed" data-id="element-89">
              Un encuentro íntimo y profundamente femenino para la mujer que ya
              no quiere seguir igual.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-90">
              <Button variant="primary" size="lg" onClick={scrollToOffer} className="shadow-lg shadow-brand-clay/20" data-id="element-91">
                Reserva tu cupo ahora
              </Button>
              <Button variant="outline" size="lg" className="text-white border-white hover:bg-white/10 focus:ring-white" onClick={() => document.getElementById('problem')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-92">
                Leer más
              </Button>
            </div>
          </div>

          <div className="flex-1 relative max-w-md w-full" data-id="element-93">
            <div className="absolute -inset-4 bg-brand-clay/20 rounded-[2rem] blur-2xl transform rotate-3" data-id="element-94"></div>
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/20" data-id="element-95">
              <img src="https://cdn.magicpatterns.com/uploads/jomajo3SSHHmkh5xF5a7j2/KATE_4Landingpage.png" alt="Kate Castrillon - Aquí entre Mujeres" className="w-full h-auto object-cover" loading="eager" decoding="async" data-id="element-96" />
            </div>
            <p className="text-white/90 font-serif text-xl md:text-2xl italic text-center mt-6" data-id="element-97">
              "No necesitas arreglarte, necesitas volver a ti."
            </p>
          </div>
        </div>
      </header>

      {/* PROBLEM AWARENESS */}
      <Section id="problem" background="white" data-id="element-98">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-99">
          <Heart className="h-8 w-8 text-brand-clay mx-auto mb-6" data-id="element-100" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-dark mb-6" data-id="element-101">
            Hay un cansancio que no se quita con dormir.
          </h2>
          <p className="text-lg text-brand-text leading-relaxed mb-6" data-id="element-102">
            Ni con "pensar positivo". Ni con aguantar un poquito más. Es ese
            cansancio de{' '}
            <strong className="text-brand-clay" data-id="element-103">
              ser fuerte todo el tiempo
            </strong>
            , de sostener a todos, de cumplir… mientras por dentro sientes que
            algo en ti pide auxilio.
          </p>
          <p className="text-lg text-brand-text leading-relaxed" data-id="element-104">
            Y lo peor no es la tristeza. Lo peor es esa sensación de que tu vida
            sigue avanzando… pero tú sigues igual.
          </p>
        </div>

        <div className="bg-brand-pistachio/30 rounded-3xl p-8 md:p-12 my-12 border border-brand-eucalyptus/30" data-id="element-105">
          <h3 className="text-2xl font-serif font-bold text-brand-dark mb-8 text-center" data-id="element-106">
            Si esto te incomoda… probablemente es para ti
          </h3>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-107">
            {['Estoy bien… pero no me siento bien.', 'Hago demasiado y aun así siento que no avanzo.', 'Quiero cambiar, pero me cuesta sostenerlo.', 'Estoy cansada de repetir los mismos patrones.'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white/80 p-4 rounded-xl shadow-sm border border-brand-ivory" data-id="element-108">
                <div className="h-6 w-6 rounded-full bg-brand-eucalyptus/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-109">
                  <div className="h-2 w-2 rounded-full bg-brand-clay" data-id="element-110"></div>
                </div>
                <p className="text-brand-dark font-medium" data-id="element-111">{item}</p>
              </div>)}
          </div>
          <div className="mt-10 text-center" data-id="element-112">
            <p className="text-xl font-serif italic text-brand-dark/80" data-id="element-113">
              "No es que te falte fuerza. Es que estás cargando cosas que no
              tendrías que estar cargando."
            </p>
            <div className="mt-8" data-id="element-114">
              <Button variant="primary" size="lg" onClick={scrollToOffer} className="shadow-lg shadow-brand-clay/20" data-id="element-115">
                Quiero reservar mi cupo
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* THE INVISIBLE LOAD */}
      <Section background="light" data-id="element-116">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-117">
          <div data-id="element-118">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-dark mb-6" data-id="element-119">
              La "carga invisible"
            </h2>
            <p className="text-lg text-brand-text mb-6" data-id="element-120">
              Es invisible porque nadie la mide. Pero tú la sientes en el pecho
              apretado cuando estás sola, en la mente que no se apaga, en el
              "sí" que dices por costumbre y en la culpa cuando descansas.
            </p>
            <p className="text-lg text-brand-text mb-8" data-id="element-121">
              Cuando una mujer lleva eso por meses (o años), se vuelve experta
              en funcionar. Y se olvida de lo que se siente estar en paz.
            </p>
            <div className="space-y-4" data-id="element-122">
              <h4 className="font-bold text-brand-clay uppercase tracking-wide text-sm" data-id="element-123">
                El resultado que quieres:
              </h4>
              <ul className="space-y-3" data-id="element-124">
                {['Sentir ligereza sin sentir culpa', 'Tomar decisiones con seguridad', 'Reconciliarte con el dinero y el merecimiento', 'Soltar lo que no es tuyo', 'Habitar tu energía femenina sin vergüenza'].map((item, i) => <li key={i} className="flex items-center gap-3 text-brand-dark" data-id="element-125">
                    <Check className="h-5 w-5 text-brand-eucalyptus" data-id="element-126" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
          <div className="relative" data-id="element-127">
            <div className="absolute -inset-4 bg-brand-peach rounded-full blur-3xl opacity-50" data-id="element-128"></div>
            <img src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1999&auto=format&fit=crop" alt="Mujer en paz y libertad" className="relative rounded-2xl shadow-xl w-full h-auto object-cover aspect-[4/5]" loading="lazy" decoding="async" data-id="element-129" />
          </div>
        </div>
      </Section>

      {/* THE SOLUTION */}
      <Section background="white" data-id="element-130">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-131">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-dark mb-4" data-id="element-132">
            El Reset Femenino
          </h2>
          <p className="text-xl text-brand-clay font-medium" data-id="element-133">
            Ordenar → Soltar → Elegirte
          </p>
          <div className="w-24 h-1 bg-brand-eucalyptus mx-auto mt-6 rounded-full" data-id="element-134"></div>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-135">
          {[{
          step: '01',
          title: 'Ordenar',
          desc: "Emociones y energía. No para 'controlarte', sino para volver a escucharte sin ruido."
        }, {
          step: '02',
          title: 'Soltar',
          desc: 'Cargas invisibles que no te pertenecen: expectativas, culpas, exigencias, roles, lealtades.'
        }, {
          step: '03',
          title: 'Elegirte',
          desc: "Decidir desde tu verdad. No desde el miedo. No desde la complacencia. No desde el 'después'."
        }].map((card, i) => <div key={i} className="bg-brand-ivory p-8 rounded-2xl border border-brand-pistachio shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden group" data-id="element-136">
              <div className="absolute top-0 right-0 p-4 opacity-10 font-serif text-9xl font-bold text-brand-clay -mr-4 -mt-4 group-hover:scale-110 transition-transform" data-id="element-137">
                {card.step}
              </div>
              <div className="relative z-10" data-id="element-138">
                <span className="text-brand-clay font-bold tracking-widest text-sm mb-2 block" data-id="element-139">
                  PASO {card.step}
                </span>
                <h3 className="text-2xl font-serif font-bold text-brand-dark mb-4" data-id="element-140">
                  {card.title}
                </h3>
                <p className="text-brand-text leading-relaxed" data-id="element-141">{card.desc}</p>
              </div>
            </div>)}
        </div>

        <div className="mt-16 bg-brand-peach/30 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 border border-brand-peach" data-id="element-142">
          <div className="flex-1" data-id="element-143">
            <h3 className="text-2xl font-serif font-bold text-brand-clay mb-4" data-id="element-144">
              Sanación de útero
            </h3>
            <p className="text-brand-dark/80 leading-relaxed" data-id="element-145">
              Este proceso toca una raíz profunda. Porque el cuerpo guarda lo
              que la mente quiere "superar rápido". Trabajaremos con respeto y
              claridad para liberar memorias y potenciar tu creatividad y
              merecimiento.
            </p>
          </div>
          <div className="flex-shrink-0" data-id="element-146">
            <div className="h-24 w-24 rounded-full bg-brand-peach flex items-center justify-center" data-id="element-147">
              <Sparkles className="h-10 w-10 text-brand-clay" data-id="element-148" />
            </div>
          </div>
        </div>
      </Section>

      {/* KATE'S PERSONAL AUDIO MESSAGE */}
      <Section background="accent" data-id="element-149">
        <div className="max-w-2xl mx-auto text-center" data-id="element-150">
          <div className="flex justify-center mb-6" data-id="element-151">
            <div className="relative" data-id="element-152">
              <div className="absolute -inset-1 bg-brand-clay/30 rounded-full blur-md" data-id="element-153"></div>
              <img src="https://cdn.magicpatterns.com/uploads/jomajo3SSHHmkh5xF5a7j2/KATE_4Landingpage.png" alt="Kate Castrillon" className="relative h-20 w-20 rounded-full object-cover border-3 border-brand-ivory shadow-lg" loading="lazy" decoding="async" data-id="element-154" />
            </div>
          </div>
          <h3 className="text-2xl font-serif font-bold text-brand-dark mb-2" data-id="element-155">
            Un mensaje de Kate para ti
          </h3>
          <p className="text-brand-text text-sm mb-6 flex items-center justify-center gap-1.5" data-id="element-156">
            <Volume2 className="h-4 w-4 text-brand-clay" data-id="element-157" />
            Escúchalo, es personal
          </p>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-brand-pistachio" data-id="element-158">
            <audio controls className="w-full" preload="metadata" onPlay={() => duck()} onPause={() => unduck()} onEnded={() => unduck()} data-id="element-159">
              <source src="https://raw.githubusercontent.com/jvzng/webpage_chatbot_closer/8285e116406af340b0cf7af9f5b9e480d1849080/Kate%20audio%204landing%20page.MP3" type="audio/mpeg" data-id="element-160" />
              Tu navegador no soporta el reproductor de audio.
            </audio>
          </div>
        </div>
      </Section>

      {/* OFFER SECTION */}
      <Section id="offer" background="dark" className="relative overflow-hidden" data-id="element-161">
        <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center" data-id="element-162">
          <div className="text-white" data-id="element-163">
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6" data-id="element-164">
              Un encuentro íntimo para transformar tu realidad
            </h2>
            <p className="text-brand-pistachio text-lg mb-8 leading-relaxed" data-id="element-165">
              Este espacio no es masivo porque esto no se trabaja en masa. La
              transformación real necesita presencia, contención y enfoque. Si
              estás leyendo esto y sientes ese "sí" por dentro… no lo negocies
              con tu miedo.
            </p>

            <div className="space-y-6 mb-10" data-id="element-166">
              <div className="flex items-start gap-4" data-id="element-167">
                <div className="h-10 w-10 rounded-full bg-brand-clay flex items-center justify-center flex-shrink-0 border border-brand-peach" data-id="element-168">
                  <ShieldCheck className="h-5 w-5 text-white" data-id="element-169" />
                </div>
                <div data-id="element-170">
                  <h4 className="font-bold text-lg text-white" data-id="element-171">
                    Espacio Seguro
                  </h4>
                  <p className="text-brand-pistachio text-sm" data-id="element-172">
                    Solo mujeres, sin juicios, confidencialidad total.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-173">
                <div className="h-10 w-10 rounded-full bg-brand-clay flex items-center justify-center flex-shrink-0 border border-brand-peach" data-id="element-174">
                  <Star className="h-5 w-5 text-white" data-id="element-175" />
                </div>
                <div data-id="element-176">
                  <h4 className="font-bold text-lg text-white" data-id="element-177">
                    Experiencia Premium
                  </h4>
                  <p className="text-brand-pistachio text-sm" data-id="element-178">
                    Materiales incluidos, coffee break, ubicación exclusiva.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center" data-id="element-179">
            <PriceCard data-id="element-180" />
          </div>
        </div>
      </Section>

      {/* FILTER: WHO IS THIS FOR */}
      <Section background="white" data-id="element-181">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-182">
          <div className="bg-brand-eucalyptus/20 p-8 rounded-3xl border border-brand-eucalyptus/40" data-id="element-183">
            <h3 className="text-xl font-serif font-bold text-brand-dark mb-6 flex items-center gap-2" data-id="element-184">
              <Check className="h-6 w-6 text-brand-clay" data-id="element-185" /> Para quién SÍ es
            </h3>
            <ul className="space-y-4" data-id="element-186">
              {['Estás cansada de cargar con todo en silencio', "Sientes que ya entendiste 'mucho', pero te falta sostener el cambio", 'Quieres un espacio femenino real, íntimo, sin juicio', 'Estás lista para soltar versiones viejas de ti'].map((item, i) => <li key={i} className="flex items-start gap-3 text-brand-dark/80" data-id="element-187">
                  <div className="h-1.5 w-1.5 rounded-full bg-brand-clay mt-2 flex-shrink-0" data-id="element-188"></div>
                  {item}
                </li>)}
            </ul>
          </div>

          <div className="bg-red-50 p-8 rounded-3xl border border-red-100" data-id="element-189">
            <h3 className="text-xl font-serif font-bold text-red-800 mb-6 flex items-center gap-2" data-id="element-190">
              <span className="text-2xl leading-none" data-id="element-191">×</span> Para quién NO es
            </h3>
            <ul className="space-y-4" data-id="element-192">
              {["Solo quieres 'una charla motivacional'", 'Quieres cambiar sin tocar nada incómodo', 'Vienes a complacer a alguien más', 'Buscas una solución mágica sin compromiso personal'].map((item, i) => <li key={i} className="flex items-start gap-3 text-red-900/80" data-id="element-193">
                  <div className="h-1.5 w-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" data-id="element-194"></div>
                  {item}
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-195">
          <Button variant="primary" size="lg" onClick={scrollToOffer} className="shadow-lg shadow-brand-clay/20" data-id="element-196">
            Quiero reservar mi cupo
          </Button>
        </div>
      </Section>

      {/* FAQ */}
      <Section background="light" data-id="element-197">
        <div className="text-center mb-12" data-id="element-198">
          <h2 className="text-3xl font-serif font-bold text-brand-dark mb-4" data-id="element-199">
            Preguntas Frecuentes
          </h2>
          <p className="text-brand-text" data-id="element-200">
            Todo lo que necesitas saber antes de reservar
          </p>
        </div>
        <div className="max-w-3xl mx-auto" data-id="element-201">
          <Accordion items={[{
          title: '¿Y si me da pena ir sola?',
          content: 'La mayoría llega sola. Este es un espacio diseñado para que te sientas cuidada, no expuesta. Desde el primer momento sentirás la contención del grupo y del equipo.'
        }, {
          title: '¿Esto es terapia?',
          content: 'No es terapia clínica. Es un evento presencial de sanación y desarrollo personal con un proceso guiado y contenido práctico diseñado para generar cambios profundos en poco tiempo.'
        }, {
          title: '¿Y si estoy en un momento muy sensible?',
          content: 'Precisamente por eso es solo mujeres y con cupos limitados: para sostener el proceso con respeto. No te obligaremos a nada que no quieras hacer.'
        }, {
          title: "¿Qué pasa si 'me remueve' cosas?",
          content: 'Ese es el punto: mover para liberar. Pero lo hacemos con guía y estructura, no para abrir y dejarte peor, sino para que salgas más ligera y ordenada.'
        }, {
          title: '¿Cuáles son los métodos de pago?',
          content: 'Aceptamos tarjetas de crédito/débito, PayPal y transferencia bancaria. Todos los detalles estarán disponibles al momento de reservar tu cupo.'
        }]} data-id="element-202" />
        </div>
      </Section>

      {/* FINAL CTA */}
      <footer className="bg-brand-dark text-white py-20 px-4 text-center pb-28" data-id="element-203">
        <div className="max-w-3xl mx-auto" data-id="element-204">
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6" data-id="element-205">
            Si ya te cansaste de seguir igual, este es el punto de quiebre.
          </h2>
          <p className="text-xl text-brand-pistachio mb-10" data-id="element-206">
            Porque tu paz no se consigue "cuando todo se calme". <br data-id="element-207" />
            Se consigue cuando tú decides.
          </p>
          <Button variant="secondary" size="lg" className="text-lg px-12 py-5 shadow-2xl shadow-brand-clay/20 bg-brand-clay text-white hover:bg-[#C89268]" onClick={() => window.open('https://pay.hotmart.com/A104324565P?off=pi9fadac', '_blank')} data-id="element-208">
            Reserva tu cupo ahora
          </Button>
          <p className="mt-8 text-sm text-brand-eucalyptus" data-id="element-209">
            Evento SOLO MUJERES — 14 de Marzo — Costa Rica 🇨🇷 — Cupos limitados
          </p>
          <a href="https://www.instagram.com/katecastrillonb/" target="_blank" rel="noopener noreferrer" onClick={e => {
          e.preventDefault();
          window.open('https://www.instagram.com/katecastrillonb/', '_blank');
        }} className="relative z-20 inline-flex items-center gap-2 mt-6 text-brand-pistachio hover:text-white transition-colors group cursor-pointer" data-id="element-210">
            <Instagram className="h-5 w-5 group-hover:scale-110 transition-transform" data-id="element-211" />
            <span className="text-sm font-medium" data-id="element-212">@katecastrillonb</span>
          </a>
        </div>
      </footer>

      {/* Sticky Spot Counter */}
      <StickySpotCounter data-id="element-213" />
    </div>;
}